import { DARK_SURFACE } from "@pinksy/contracts";
import { overlayBrandColours, withReadableOnColours, type AppBranding } from "@/app/theme";

interface ThemeColors {
  dark: boolean;
  colors: Record<string, string>;
}

interface PaletteColors {
  primary: string;
  secondary: string;
  tertiary: string;
  background: string;
  foreground: string;
  navbar: string;
  /**
   * `surface` is the token EVERY Vuetify component paints itself with —
   * v-card, v-list, v-menu, v-app-bar, v-navigation-drawer. `card`/`raised`
   * are the house aliases the modules use.
   */
  surface: string;
  card: string;
  raised: string;
  /** Explicit, so Vuetify does not derive a contrast colour of its own. */
  "on-surface": string;
  "on-background": string;
  primaryText: string;
  lightText: string;
  darkText: string;
  /** Status colours. */
  error: string;
  success: string;
  warning: string;
  info: string;
  alert: string;
  edit: string;
}

/**
 * The shell's OWN palette — what a page with no resolved tenant (an unknown
 * address, a network failure) and the pre-resolve chrome are painted with.
 * Source of truth, per colour: the PRIMARY is the brand pack (`brand/tokens/`),
 * and everything else is still site/plan.html's dark palette (`--blue`, `--gold`,
 * `--green` and the ink/paper neutrals). That split is deliberate and is stated
 * here because the line above used to name plan.html for all of them: plan.html's
 * `--pink` is one of four DOCUMENT accents, used once, and never was the brand's
 * pink. `services/tenant` `DEFAULT_TENANT_BRANDING` carries the same
 * primary/secondary for a tenant row that sets no colours. A resolved tenant's
 * colours overlay these (build plan §7: never bake a palette into a module — the
 * shell is the one place a default lives).
 *
 * 🔴 **THE PRIMARY IS PINKSY PINK, MEASURED OFF THE ARTWORK** (identity manual v1.0).
 * It was `#E58CA2`, a dusty rose that matched no shipped mark — the brand pack's
 * `pink-500` is `#F3418A`, recovered from `brand/logov2.png` by least-squares fit and
 * pinned in `brand/tokens/pinksy-tokens.json`. This is the colour an UNRESOLVED tenant
 * is painted with, so it is the one screen where Pinksy's own pink is correct: the
 * moment a tenant resolves, their colours overlay it.
 *
 * Secondary and tertiary are deliberately unchanged. The identity manual defines one
 * pink and a neutral ramp; it says nothing about a secondary, and inventing one here
 * would be a palette decision wearing a brand decision's clothes.
 */
export const DEFAULT_PRIMARY = "#F3418A";
export const DEFAULT_SECONDARY = "#89ADD9";
const DEFAULT_TERTIARY = "#75B994";

const SHARED_SOURCE = {
  primary: DEFAULT_PRIMARY,
  secondary: DEFAULT_SECONDARY,
  tertiary: DEFAULT_TERTIARY,
  error: "#DC4C41",
  success: DEFAULT_TERTIARY,
  warning: "#D6B24C",
  info: DEFAULT_SECONDARY,
  alert: "#DC4C41",
  edit: "#3D6390",
  lightText: "#FFFFFF",
  darkText: "#16191D",
};

const LIGHT_SOURCE: PaletteColors = {
  ...SHARED_SOURCE,
  background: "#E9E7E2",
  foreground: "#FBFAF8",
  surface: "#FBFAF8",
  card: "#FBFAF8",
  raised: "#FFFFFF",
  navbar: "#2B3037",
  "on-surface": "#16191D",
  "on-background": "#16191D",
  primaryText: "#16191D",
};

/**
 * 🔴 **THE DARK SURFACE IS A NAMED CONSTANT, NOT A HEX** (D139). The logo analysis measures whether a
 * company's mark reads on THIS colour, so both read the one `DARK_SURFACE` in `@pinksy/contracts` —
 * change the surface there and "reads on dark" is re-measured against the colour actually painted.
 */
const DARK_SOURCE: PaletteColors = {
  ...SHARED_SOURCE,
  background: "#131619",
  foreground: DARK_SURFACE,
  surface: DARK_SURFACE,
  card: DARK_SURFACE,
  raised: "#2B3037",
  navbar: DARK_SURFACE,
  "on-surface": "#E9E7E2",
  "on-background": "#E9E7E2",
  primaryText: "#E9E7E2",
};

/** Vuetify-4 shaped `{ light, dark }` themes, the app's brand colours overlaid. */
export function buildTenantThemes(branding: AppBranding | null): { light: ThemeColors; dark: ThemeColors } {
  const light: ThemeColors = { dark: false, colors: { ...LIGHT_SOURCE, accent: LIGHT_SOURCE.tertiary } };
  const dark: ThemeColors = { dark: true, colors: { ...DARK_SOURCE, accent: DARK_SOURCE.tertiary } };
  // The `on-` colours are chosen AFTER the brand overlay, so a white-label primary gets its own (round 3).
  const readable = (colors: Record<string, string>): Record<string, string> => withReadableOnColours(colors, SHARED_SOURCE.lightText, SHARED_SOURCE.darkText);
  if (!branding) return { light: { dark: false, colors: readable(light.colors) }, dark: { dark: true, colors: readable(dark.colors) } };
  return {
    light: { dark: false, colors: readable(overlayBrandColours(light.colors, false, branding)) },
    dark: { dark: true, colors: readable(overlayBrandColours(dark.colors, true, branding)) },
  };
}

function upsertMeta(name: string, content: string): void {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertFavicon(href: string): void {
  let el = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!el) {
    el = document.createElement("link");
    el.rel = "icon";
    document.head.appendChild(el);
  }
  el.href = href;
}

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

/** Only a strict `#rrggbb` value is trusted from the branding — anything else falls back to the shell's own default. */
function safeColour(value: string | null | undefined, fallback: string): string {
  return value && HEX_COLOUR.test(value) ? value : fallback;
}

/**
 * Does this app stand under its OWN name and mark? A WHITE_LABEL app always
 * does — it is the customer's brand and must never show ours. A PRODUCT app
 * is "Pinksy | <app>" unless its branding says `identity: "own"` (both launch
 * tenants do — services/tenant defaults to it). Every place the shell decides
 * whether to draw the vendor name or mark reads THIS, never `kind` directly.
 */
export function hasOwnIdentity(
  kind: "WHITE_LABEL" | "PRODUCT" | null | undefined,
  branding: Pick<AppBranding, "identity"> | null | undefined,
): boolean {
  return kind === "WHITE_LABEL" || branding?.identity === "own";
}

/**
 * Brand the DOM: title, favicon, theme-color, and `:root` CSS vars every
 * module's brand overlay + the shell's own chrome read from.
 * Falls back to the shell's defaults when a colour is unset OR malformed
 * (branding contract: all keys nullable; a bad value here would otherwise
 * land in a `style.setProperty()` call and/or a Vuetify theme colour
 * completely unvalidated).
 */
export function applyBranding(
  name: string,
  branding: AppBranding | null,
  root: HTMLElement = document.documentElement,
): void {
  document.title = name;

  const defaultThemeIsDark = branding?.defaultTheme !== "light";
  const source = defaultThemeIsDark ? DARK_SOURCE : LIGHT_SOURCE;
  const colours = branding?.colours;

  const primary = safeColour(colours?.primary, source.primary);
  const secondary = safeColour(colours?.secondary, source.secondary);
  const tertiary = safeColour(colours?.tertiary, source.tertiary);
  const background = safeColour(colours?.background, source.background);
  const surface = safeColour(colours?.surface, source.foreground);
  const navbar = safeColour(colours?.navbar, source.navbar);

  root.style.setProperty("--tenant-primary", primary);
  root.style.setProperty("--tenant-secondary", secondary);
  root.style.setProperty("--tenant-tertiary", tertiary);
  root.style.setProperty("--tenant-background", background);
  root.style.setProperty("--tenant-surface", surface);
  root.style.setProperty("--tenant-navbar", navbar);

  upsertMeta("theme-color", navbar);
  if (branding?.iconUrl) upsertFavicon(branding.iconUrl);
}
